/**
 * Compact per-module current progress summary for the dashboard.
 *
 * @module LatestModuleProgressSummary
 */

import type { CSSProperties } from 'react';
import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import {
  SIMULATION_MODULE_COLORS,
  type LatestModuleProgress,
  type SimulationModuleKey,
} from '@/lib/simulationResultsChartData';
import { SIMULATION_MODULE_ICONS, SIMULATION_MODULE_SHORT_LABELS } from '@/lib/simulationModuleDisplay';
import { cn } from '@/lib/utils';

export interface LatestModuleProgressSummaryProps {
  readonly items: LatestModuleProgress[];
  /** AC4: open detailed review for the module's latest graded attempt. */
  readonly onModuleClick?: (sessionId: string, module: SimulationModuleKey) => void;
}

function formatModuleDate(date: string | null) {
  if (!date) return null;
  return new Date(date).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

export function LatestModuleProgressSummary({ items, onModuleClick }: LatestModuleProgressSummaryProps) {
  const { t } = useTranslation();

  return (
    <section className="space-y-3" aria-labelledby="latest-module-progress-title">
      <div className="flex items-center justify-between gap-3">
        <h3 id="latest-module-progress-title" className="font-ui text-sm font-semibold text-foreground">
          {t('dashboard.insights.latestByModule', { defaultValue: 'Latest by module' })}
        </h3>
      </div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {items.map((item) => {
          const Icon = SIMULATION_MODULE_ICONS[item.module];
          const color = SIMULATION_MODULE_COLORS[item.module];
          const moduleLabel = t(`moduleSelection.modules.${item.module}.label`, {
            defaultValue: SIMULATION_MODULE_SHORT_LABELS[item.module],
          });
          const dateLabel = formatModuleDate(item.date);
          const hasScore = item.status === 'graded' && typeof item.percentage === 'number';
          const canOpenReview =
            Boolean(onModuleClick) && item.status === 'graded' && Boolean(item.sessionId);

          const reviewAriaLabel = t('simulation.results.performanceTrends.openModuleReview', {
            module: moduleLabel,
            defaultValue: 'Review latest {{module}} attempt',
          });

          const cardBody = (
            <>
              <div className="flex min-w-0 items-center gap-2">
                <div
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-border bg-background"
                  style={{ color } as CSSProperties}
                >
                  <Icon className="h-4 w-4" aria-hidden />
                </div>
                <div className="min-w-0">
                  <p className="font-ui truncate text-xs font-medium text-muted-foreground">
                    {moduleLabel}
                  </p>
                  <span
                    className={cn(
                      'font-ui mt-0.5 block text-xl font-bold tracking-tight tabular-nums',
                      hasScore ? 'text-foreground' : 'text-muted-foreground',
                    )}
                  >
                    {hasScore ? `${item.percentage}%` : '—'}
                  </span>
                </div>
              </div>

              <div className="mt-3 flex min-h-5 items-center justify-between gap-2">
                {item.status === 'grading' || item.status === 'failed' ? (
                  <Badge
                    variant="secondary"
                    className={cn(
                      'font-ui px-2 py-0 text-[10px] font-medium',
                      item.status === 'failed' && 'border-destructive/30 bg-destructive/10 text-destructive',
                    )}
                  >
                    {item.status === 'failed' ? (
                      <AlertTriangle className="mr-1 h-3 w-3" aria-hidden />
                    ) : null}
                    {item.status === 'failed'
                      ? t('simulation.results.status.failed', { defaultValue: 'Analysis failed' })
                      : t('simulation.results.status.analysing', { defaultValue: 'Analysing...' })}
                  </Badge>
                ) : (
                  <span className="font-ui truncate text-[11px] text-muted-foreground">
                    {dateLabel ?? t('dashboard.insights.notAttempted', { defaultValue: 'Not attempted' })}
                  </span>
                )}
                {item.status === 'graded' && item.passed !== null ? (
                  <span
                    className={cn(
                      'font-ui shrink-0 text-[11px] font-semibold',
                      item.passed ? 'text-success' : 'text-destructive',
                    )}
                  >
                    {item.passed
                      ? t('common.passed', { defaultValue: 'Passed' })
                      : t('common.failed', { defaultValue: 'Failed' })}
                  </span>
                ) : null}
              </div>
            </>
          );

          if (canOpenReview && item.sessionId) {
            return (
              <button
                key={item.module}
                type="button"
                onClick={() => onModuleClick?.(item.sessionId!, item.module)}
                aria-label={reviewAriaLabel}
                className={cn(
                  'min-w-0 rounded-lg border border-border bg-card p-3 text-left shadow-sm transition-colors',
                  'hover:border-primary/40 hover:bg-accent/40',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                )}
              >
                {cardBody}
              </button>
            );
          }

          return (
            <div
              key={item.module}
              className="min-w-0 rounded-lg border border-border bg-card p-3 shadow-sm"
            >
              {cardBody}
            </div>
          );
        })}
      </div>
    </section>
  );
}
